import React, { useState, useEffect, useCallback } from 'react';
import { Card, CardContent, CardHeader, CardTitle, CardDescription } from '../components/ui/card';
import { Button } from '../components/ui/button';
import { Input } from '../components/ui/input';
import { Badge } from '../components/ui/badge';
import { Slider } from '../components/ui/slider';
import axios from 'axios';
import { 
  Upload, Download, Play, Pause, RefreshCw, Check, AlertCircle, Loader2,
  User, Wand2, Film, ChevronRight, ChevronLeft, Trash2, Edit3, Plus,
  Scissors, Move, RotateCcw, Settings, Layers, Zap
} from 'lucide-react';

const API = process.env.REACT_APP_BACKEND_URL;

// Animation preset categories with icons
const CATEGORY_ICONS = {
  movement: '🏃',
  punch: '👊',
  kick: '🦵',
  defense: '🛡️',
  reaction: '💥'
};

export default function PuppetLab() {
  // Character state
  const [characterId, setCharacterId] = useState(null);
  const [characterName, setCharacterName] = useState('');
  const [characterStatus, setCharacterStatus] = useState(null);
  const [sourceImages, setSourceImages] = useState({});
  const [segments, setSegments] = useState([]);
  const [puppetFile, setPuppetFile] = useState(null);
  const [poseVisualizationUrl, setPoseVisualizationUrl] = useState(null);
  
  // Existing characters
  const [existingCharacters, setExistingCharacters] = useState([]);
  
  // UI state
  const [mode, setMode] = useState('setup'); // setup, segments, animate
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState(null);
  const [presets, setPresets] = useState({});
  const [selectedPreset, setSelectedPreset] = useState(null);
  const [animations, setAnimations] = useState({});
  
  // Load animation presets and existing characters on mount
  useEffect(() => {
    loadPresets();
    loadExistingCharacters();
  }, []);
  
  // Load existing characters from database
  const loadExistingCharacters = async () => {
    try {
      const resp = await axios.get(`${API}/api/puppet-lab/characters`);
      setExistingCharacters(resp.data.characters || []);
    } catch (err) {
      console.error('Failed to load characters:', err);
    }
  };
  
  // Select an existing character
  const selectCharacter = async (char) => {
    setLoading(true);
    setCharacterId(char.id);
    setCharacterName(char.name);
    setCharacterStatus(char.status);
    
    // Load full character details
    try {
      const resp = await axios.get(`${API}/api/puppet-lab/character/${char.id}/status`);
      setCharacterStatus(resp.data.status);
      setPuppetFile(resp.data.puppet_file);
      setSourceImages(resp.data.source_images || {});
      setSegments(resp.data.segments || []);
      setAnimations(resp.data.animations || {});
      setPoseVisualizationUrl(resp.data.pose_visualization_url);
      
      // Set appropriate mode based on status
      if (resp.data.status === 'puppet_ready') {
        setMode('animate');
      } else if (resp.data.status === 'segments_ready') {
        setMode('segments');
      } else {
        setMode('setup');
      }
    } catch (err) {
      console.error('Failed to load character details:', err);
      setError('Failed to load character details');
    } finally {
      setLoading(false);
    }
  };
  
  // Reset to create new character
  const resetCharacter = () => {
    setCharacterId(null);
    setCharacterName('');
    setCharacterStatus(null);
    setSourceImages({});
    setSegments([]);
    setPuppetFile(null);
    setPoseVisualizationUrl(null);
    setMode('setup');
    setAnimations({});
    loadExistingCharacters();
  };
  
  const loadPresets = async () => {
    try {
      const resp = await axios.get(`${API}/api/puppet-lab/presets`);
      setPresets(resp.data.presets);
    } catch (err) {
      console.error('Failed to load presets:', err);
    }
  };
  
  // Create new character
  const createCharacter = async () => {
    if (!characterName.trim()) {
      setError('Please enter a character name');
      return;
    }
    
    setLoading(true);
    setError(null);
    
    try {
      const formData = new FormData();
      formData.append('name', characterName);
      
      const resp = await axios.post(`${API}/api/puppet-lab/character`, formData);
      setCharacterId(resp.data.character_id);
      setCharacterStatus('created');
    } catch (err) {
      setError(err.response?.data?.detail || 'Failed to create character');
    } finally {
      setLoading(false);
    }
  };
  
  // Upload source image
  const uploadSourceImage = async (viewType, file) => {
    if (!characterId || !file) return;
    
    setLoading(true);
    setError(null);
    
    try {
      const formData = new FormData();
      formData.append('view_type', viewType);
      formData.append('file', file);
      
      const resp = await axios.post(
        `${API}/api/puppet-lab/character/${characterId}/upload-source`,
        formData,
        { headers: { 'Content-Type': 'multipart/form-data' } }
      );
      
      setSourceImages(prev => ({
        ...prev,
        [viewType]: resp.data.image_url
      }));
      setCharacterStatus('source_uploaded');
    } catch (err) {
      setError(err.response?.data?.detail || 'Failed to upload image');
    } finally {
      setLoading(false);
    }
  };
  
  // Auto-segment character
  const autoSegment = async () => {
    if (!characterId) return;
    
    setLoading(true);
    setError(null);
    
    try {
      await axios.post(`${API}/api/puppet-lab/character/${characterId}/auto-segment`);
      setCharacterStatus('segmenting');
      
      // Poll for completion
      pollStatus();
    } catch (err) {
      setError(err.response?.data?.detail || 'Failed to start segmentation');
      setLoading(false);
    }
  };
  
  // Poll character status
  const pollStatus = useCallback(async () => {
    if (!characterId) return;
    
    try {
      const resp = await axios.get(`${API}/api/puppet-lab/character/${characterId}/status`);
      setCharacterStatus(resp.data.status);
      setSegments(resp.data.segments || []);
      setPuppetFile(resp.data.puppet_file);
      setPoseVisualizationUrl(resp.data.pose_visualization_url);
      
      if (resp.data.status === 'segmenting' || resp.data.status === 'creating_puppet') {
        setTimeout(pollStatus, 2000);
      } else {
        setLoading(false);
        if (resp.data.status === 'segments_ready') {
          setMode('segments');
        } else if (resp.data.status === 'puppet_ready') {
          setMode('animate');
        } else if (resp.data.status === 'segmentation_failed') {
          setError(`Segmentation failed: ${resp.data.error || 'Unknown error'}`);
        }
      }
    } catch (err) {
      setLoading(false);
      setError('Failed to check status');
    }
  }, [characterId]);
  
  // Get segments for editing
  const loadSegments = async () => {
    if (!characterId) return;
    
    try {
      const resp = await axios.get(`${API}/api/puppet-lab/character/${characterId}/segments`);
      setSegments(resp.data.segments);
    } catch (err) {
      console.error('Failed to load segments:', err);
    }
  };
  
  // Upload custom segments
  const uploadCustomSegments = async (files) => {
    if (!characterId || !files.length) return;
    
    setLoading(true);
    setError(null);
    
    try {
      const formData = new FormData();
      for (const file of files) {
        formData.append('files', file);
      }
      
      const resp = await axios.post(
        `${API}/api/puppet-lab/character/${characterId}/upload-segments`,
        formData,
        { headers: { 'Content-Type': 'multipart/form-data' } }
      );
      
      setSegments(resp.data.segments.map(name => ({ name })));
      setCharacterStatus('segments_ready');
      setMode('segments');
    } catch (err) {
      setError(err.response?.data?.detail || 'Failed to upload segments');
    } finally {
      setLoading(false);
    }
  };
  
  // Replace a single segment
  const replaceSegment = async (segmentName, file) => {
    if (!characterId || !segmentName || !file) return;
    
    setLoading(true);
    setError(null);
    
    try {
      const formData = new FormData();
      formData.append('file', file);
      
      const resp = await axios.post(
        `${API}/api/puppet-lab/character/${characterId}/replace-segment/${segmentName}`,
        formData,
        { headers: { 'Content-Type': 'multipart/form-data' } }
      );
      
      // Update the segment in state
      setSegments(prev => prev.map(seg => 
        seg.name === segmentName ? { ...seg, image_url: resp.data.image_url } : seg
      ));
    } catch (err) {
      setError(err.response?.data?.detail || 'Failed to replace segment');
    } finally {
      setLoading(false);
    }
  };
  
  // Create puppet rig
  const createPuppet = async () => {
    if (!characterId) return;
    
    setLoading(true);
    setError(null);
    
    try {
      await axios.post(`${API}/api/puppet-lab/character/${characterId}/create-puppet`);
      setCharacterStatus('creating_puppet');
      pollStatus();
    } catch (err) {
      setError(err.response?.data?.detail || 'Failed to create puppet');
      setLoading(false);
    }
  };
  
  // Animate puppet
  const animatePuppet = async (presetId) => {
    if (!characterId || !presetId) return;
    
    setLoading(true);
    setError(null);
    
    try {
      const formData = new FormData();
      formData.append('preset_id', presetId);
      formData.append('fps', 24);
      
      const resp = await axios.post(
        `${API}/api/puppet-lab/character/${characterId}/animate`,
        formData
      );
      
      const animationId = resp.data.animation_id;
      setAnimations(prev => ({
        ...prev,
        [animationId]: { ...resp.data, status: 'rendering' }
      }));
      
      // Poll animation status
      pollAnimationStatus(animationId);
    } catch (err) {
      setError(err.response?.data?.detail || 'Failed to animate');
      setLoading(false);
    }
  };
  
  // Poll animation status
  const pollAnimationStatus = async (animationId) => {
    if (!characterId) return;
    
    try {
      const resp = await axios.get(
        `${API}/api/puppet-lab/character/${characterId}/animation/${animationId}`
      );
      
      setAnimations(prev => ({
        ...prev,
        [animationId]: resp.data
      }));
      
      if (resp.data.status === 'rendering') {
        setTimeout(() => pollAnimationStatus(animationId), 3000);
      } else {
        setLoading(false);
      }
    } catch (err) {
      setLoading(false);
    }
  };
  
  return (
    <div className="min-h-screen bg-zinc-950 text-white p-6">
      <div className="max-w-7xl mx-auto">
        {/* Header */}
        <div className="flex items-center justify-between mb-6">
          <div>
            <h1 className="text-3xl font-bold bg-gradient-to-r from-orange-400 to-red-500 bg-clip-text text-transparent">
              Puppet Lab
            </h1>
            <p className="text-zinc-400 text-sm">2D Character Puppet Animation System</p>
          </div>
          <Badge variant="outline" className="border-orange-500 text-orange-500">
            Beta
          </Badge>
        </div>
        
        {/* Progress Steps */}
        <div className="flex gap-2 mb-6">
          {['1. Setup', '2. Segments', '3. Animate'].map((step, i) => (
            <Badge 
              key={step}
              variant={
                (mode === 'setup' && i === 0) ||
                (mode === 'segments' && i === 1) ||
                (mode === 'animate' && i === 2)
                  ? 'default' : 'secondary'
              }
              className="px-3 py-1"
            >
              {step}
            </Badge>
          ))}
        </div>
        
        {/* Error Display */}
        {error && (
          <div className="mb-4 p-3 bg-red-900/50 border border-red-500 rounded-lg flex items-center gap-2">
            <AlertCircle className="w-5 h-5 text-red-500" />
            <span className="text-red-200">{error}</span>
            <Button variant="ghost" size="sm" onClick={() => setError(null)} className="ml-auto">
              ×
            </Button>
          </div>
        )}
        
        {/* SETUP MODE */}
        {mode === 'setup' && (
          <div className="grid grid-cols-1 lg:grid-cols-2 gap-6">
            {/* Existing Characters */}
            {existingCharacters.length > 0 && !characterId && (
              <Card className="bg-zinc-900 border-zinc-800 lg:col-span-2">
                <CardHeader>
                  <CardTitle className="flex items-center gap-2">
                    <Layers className="w-5 h-5" />
                    Your Characters
                  </CardTitle>
                  <CardDescription>
                    Select an existing character to continue working on it
                  </CardDescription>
                </CardHeader>
                <CardContent>
                  <div className="grid grid-cols-2 md:grid-cols-4 lg:grid-cols-6 gap-3">
                    {existingCharacters.map((char) => (
                      <button
                        key={char.id}
                        onClick={() => selectCharacter(char)}
                        className="p-3 bg-zinc-800 rounded-lg border border-zinc-700 hover:border-orange-500 transition-colors text-left"
                      >
                        <div className="w-full aspect-square bg-zinc-900 rounded mb-2 flex items-center justify-center">
                          <User className="w-8 h-8 text-zinc-600" />
                        </div>
                        <p className="text-sm font-medium truncate">{char.name}</p>
                        <Badge 
                          variant={char.status === 'puppet_ready' ? 'default' : 'secondary'}
                          className="text-xs mt-1"
                        >
                          {char.status?.replace(/_/g, ' ')}
                        </Badge>
                      </button>
                    ))}
                  </div>
                </CardContent>
              </Card>
            )}
            
            {/* Create Character */}
            <Card className="bg-zinc-900 border-zinc-800">
              <CardHeader>
                <CardTitle className="flex items-center gap-2">
                  <User className="w-5 h-5" />
                  {characterId ? 'Current Character' : 'Create New Character'}
                </CardTitle>
                <CardDescription>
                  {characterId ? `Working on: ${characterName}` : 'Start by naming your character'}
                </CardDescription>
              </CardHeader>
              <CardContent className="space-y-4">
                {!characterId ? (
                  <>
                    <Input
                      placeholder="Character name (e.g., Fighter Joe)"
                      value={characterName}
                      onChange={(e) => setCharacterName(e.target.value)}
                      className="bg-zinc-800 border-zinc-700"
                    />
                    <Button 
                      onClick={createCharacter} 
                      disabled={loading || !characterName.trim()}
                      className="w-full"
                    >
                      {loading ? <Loader2 className="w-4 h-4 animate-spin mr-2" /> : <Plus className="w-4 h-4 mr-2" />}
                      Create Character
                    </Button>
                  </>
                ) : (
                  <div className="space-y-3">
                    <div className="flex items-center gap-2 text-green-400">
                      <Check className="w-5 h-5" />
                      <span className="font-medium">{characterName}</span>
                    </div>
                    <div className="flex items-center gap-2 text-sm text-zinc-400">
                      <span>Status:</span>
                      <Badge variant="secondary">{characterStatus?.replace(/_/g, ' ')}</Badge>
                    </div>
                    <Button 
                      variant="outline" 
                      onClick={resetCharacter}
                      className="w-full"
                    >
                      <RotateCcw className="w-4 h-4 mr-2" />
                      Switch Character
                    </Button>
                  </div>
                )}
              </CardContent>
            </Card>
            
            {/* Upload Source Images */}
            <Card className="bg-zinc-900 border-zinc-800">
              <CardHeader>
                <CardTitle className="flex items-center gap-2">
                  <Upload className="w-5 h-5" />
                  Upload Character Images
                </CardTitle>
                <CardDescription>
                  Upload front view (required), plus optional quarter and profile views
                </CardDescription>
              </CardHeader>
              <CardContent className="space-y-4">
                {['front', 'quarter', 'profile'].map((viewType) => (
                  <div key={viewType} className="flex items-center gap-3">
                    <div className="w-20 h-20 bg-zinc-800 rounded-lg overflow-hidden flex items-center justify-center border border-zinc-700">
                      {sourceImages[viewType] ? (
                        <img src={sourceImages[viewType]} alt={viewType} className="w-full h-full object-cover" />
                      ) : (
                        <User className="w-8 h-8 text-zinc-600" />
                      )}
                    </div>
                    <div className="flex-1">
                      <p className="text-sm font-medium capitalize">
                        {viewType} View {viewType === 'front' && <span className="text-red-400">*</span>}
                      </p>
                      <p className="text-xs text-zinc-500">
                        {viewType === 'front' ? 'Required' : 'Optional'}
                      </p>
                    </div>
                    <label className="cursor-pointer">
                      <Button variant="outline" size="sm" disabled={!characterId || loading} asChild>
                        <span>
                          <Upload className="w-4 h-4 mr-1" />
                          Upload
                        </span>
                      </Button>
                      <input
                        type="file"
                        accept="image/*"
                        className="hidden"
                        disabled={!characterId}
                        onChange={(e) => {
                          if (e.target.files[0]) uploadSourceImage(viewType, e.target.files[0]);
                        }}
                      />
                    </label>
                  </div>
                ))}
              </CardContent>
            </Card>
            
            {/* Segmentation Options */}
            <Card className="bg-zinc-900 border-zinc-800 lg:col-span-2">
              <CardHeader>
                <CardTitle className="flex items-center gap-2">
                  <Scissors className="w-5 h-5" />
                  Character Segmentation
                </CardTitle>
                <CardDescription>
                  Split your character into animatable body parts
                </CardDescription>
              </CardHeader>
              <CardContent>
                <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
                  {/* Auto Segment */}
                  <div className="p-4 bg-zinc-800 rounded-lg border border-zinc-700">
                    <h3 className="font-semibold mb-2 flex items-center gap-2">
                      <Wand2 className="w-4 h-4 text-purple-400" />
                      Auto-Segment
                    </h3>
                    <p className="text-sm text-zinc-400 mb-3">
                      AI automatically detects and segments body parts from your front view image.
                    </p>
                    <Button 
                      onClick={autoSegment}
                      disabled={!sourceImages.front || loading}
                      className="w-full"
                    >
                      {loading && characterStatus === 'segmenting' ? (
                        <><Loader2 className="w-4 h-4 animate-spin mr-2" /> Segmenting...</>
                      ) : (
                        <><Wand2 className="w-4 h-4 mr-2" /> Auto-Segment</>
                      )}
                    </Button>
                  </div>
                  
                  {/* Upload Segments */}
                  <div className="p-4 bg-zinc-800 rounded-lg border border-zinc-700">
                    <h3 className="font-semibold mb-2 flex items-center gap-2">
                      <Layers className="w-4 h-4 text-blue-400" />
                      Upload Pre-Segmented
                    </h3>
                    <p className="text-sm text-zinc-400 mb-3">
                      Upload your own segmented layers (head.png, torso.png, etc.)
                    </p>
                    <label className="cursor-pointer">
                      <Button variant="outline" className="w-full" disabled={!characterId || loading} asChild>
                        <span>
                          <Upload className="w-4 h-4 mr-2" />
                          Upload Segments
                        </span>
                      </Button>
                      <input
                        type="file"
                        accept="image/*"
                        multiple
                        className="hidden"
                        disabled={!characterId}
                        onChange={(e) => {
                          if (e.target.files.length) uploadCustomSegments(Array.from(e.target.files));
                        }}
                      />
                    </label>
                  </div>
                </div>
                
                {/* Expected segments list */}
                <div className="mt-4 p-3 bg-zinc-800/50 rounded-lg">
                  <p className="text-xs text-zinc-500 mb-2">Expected segment names for upload:</p>
                  <p className="text-xs text-zinc-400 font-mono">
                    head, torso, left_upper_arm, left_forearm, left_hand, right_upper_arm, right_forearm, right_hand, left_thigh, left_calf, left_foot, right_thigh, right_calf, right_foot
                  </p>
                </div>
              </CardContent>
            </Card>
          </div>
        )}
        
        {/* SEGMENTS MODE */}
        {mode === 'segments' && (
          <div className="space-y-6">
            {/* Pose Visualization - Debug/Review */}
            {poseVisualizationUrl && (
              <Card className="bg-zinc-900 border-zinc-800">
                <CardHeader>
                  <CardTitle className="flex items-center gap-2">
                    <User className="w-5 h-5" />
                    Pose Detection Result
                  </CardTitle>
                  <CardDescription>
                    DWPose skeleton detection - shows detected keypoints used for segmentation
                  </CardDescription>
                </CardHeader>
                <CardContent>
                  <div className="flex gap-4">
                    <div className="flex-1">
                      <p className="text-xs text-zinc-500 mb-2">Original Image</p>
                      <div className="aspect-square max-w-md bg-zinc-800 rounded-lg overflow-hidden">
                        {sourceImages.front && (
                          <img src={sourceImages.front} alt="Original" className="w-full h-full object-contain" />
                        )}
                      </div>
                    </div>
                    <div className="flex-1">
                      <p className="text-xs text-zinc-500 mb-2">Detected Pose (Skeleton)</p>
                      <div className="aspect-square max-w-md bg-zinc-800 rounded-lg overflow-hidden">
                        <img src={poseVisualizationUrl} alt="Pose Detection" className="w-full h-full object-contain" />
                      </div>
                    </div>
                  </div>
                </CardContent>
              </Card>
            )}
            
            <Card className="bg-zinc-900 border-zinc-800">
              <CardHeader>
                <CardTitle className="flex items-center gap-2">
                  <Layers className="w-5 h-5" />
                  Review Segments
                </CardTitle>
                <CardDescription>
                  Review and edit the segmented body parts. Click "Replace" to upload a new image for any segment.
                </CardDescription>
              </CardHeader>
              <CardContent>
                <div className="grid grid-cols-2 md:grid-cols-4 lg:grid-cols-7 gap-3">
                  {segments.map((seg, i) => (
                    <div 
                      key={i} 
                      className="p-2 bg-zinc-800 rounded-lg border border-zinc-700 hover:border-zinc-500"
                    >
                      <div className="aspect-square bg-zinc-900 rounded mb-2 flex items-center justify-center overflow-hidden">
                        {seg.image_url ? (
                          <img src={seg.image_url} alt={seg.name} className="w-full h-full object-contain" />
                        ) : (
                          <User className="w-8 h-8 text-zinc-600" />
                        )}
                      </div>
                      <p className="text-xs text-center truncate mb-2">{seg.name || seg}</p>
                      <label className="cursor-pointer">
                        <Button variant="outline" size="sm" className="w-full text-xs h-7" asChild>
                          <span>
                            <Upload className="w-3 h-3 mr-1" />
                            Replace
                          </span>
                        </Button>
                        <input
                          type="file"
                          accept="image/png"
                          className="hidden"
                          onChange={(e) => {
                            if (e.target.files[0]) {
                              replaceSegment(seg.name, e.target.files[0]);
                            }
                          }}
                        />
                      </label>
                    </div>
                  ))}
                </div>
                
                {/* Status Progress Indicator */}
                {(characterStatus === 'creating_puppet' || characterStatus === 'segmenting') && (
                  <div className="mt-6 p-4 bg-zinc-800 rounded-lg border border-zinc-700">
                    <div className="flex items-center gap-3 mb-3">
                      <Loader2 className="w-5 h-5 animate-spin text-orange-500" />
                      <span className="font-medium">
                        {characterStatus === 'creating_puppet' ? 'Creating Puppet Rig...' : 'Auto-Segmenting...'}
                      </span>
                    </div>
                    <div className="w-full bg-zinc-700 rounded-full h-2 mb-2">
                      <div className="bg-orange-500 h-2 rounded-full animate-pulse" style={{ width: '60%' }}></div>
                    </div>
                    <p className="text-xs text-zinc-400">
                      {characterStatus === 'creating_puppet' 
                        ? 'Blender is assembling your puppet with bones and rigging. This may take 30-60 seconds...'
                        : 'AI is detecting body parts and creating masks. This may take 30-60 seconds...'}
                    </p>
                  </div>
                )}
                
                <div className="flex gap-3 mt-6">
                  <Button variant="outline" onClick={() => setMode('setup')}>
                    <ChevronLeft className="w-4 h-4 mr-1" /> Back
                  </Button>
                  <Button variant="outline" onClick={loadSegments}>
                    <RefreshCw className="w-4 h-4 mr-1" /> Refresh
                  </Button>
                  <div className="flex-1" />
                  <Button onClick={createPuppet} disabled={loading || segments.length === 0}>
                    {loading && characterStatus === 'creating_puppet' ? (
                      <><Loader2 className="w-4 h-4 animate-spin mr-2" /> Creating Puppet...</>
                    ) : (
                      <><Zap className="w-4 h-4 mr-2" /> Create Puppet Rig</>
                    )}
                  </Button>
                </div>
              </CardContent>
            </Card>
            
            {/* Download segments for editing */}
            <Card className="bg-zinc-900 border-zinc-800">
              <CardHeader>
                <CardTitle className="flex items-center gap-2">
                  <Download className="w-5 h-5" />
                  Download & Edit
                </CardTitle>
                <CardDescription>
                  Download segments, edit in your image editor, then re-upload
                </CardDescription>
              </CardHeader>
              <CardContent>
                <div className="flex flex-wrap gap-2">
                  {segments.map((seg, i) => (
                    seg.image_url && (
                      <a 
                        key={i}
                        href={seg.image_url} 
                        download={`${seg.name || seg}.png`}
                        className="inline-flex"
                      >
                        <Button variant="outline" size="sm">
                          <Download className="w-3 h-3 mr-1" />
                          {seg.name || seg}
                        </Button>
                      </a>
                    )
                  ))}
                </div>
              </CardContent>
            </Card>
          </div>
        )}
        
        {/* ANIMATE MODE */}
        {mode === 'animate' && (
          <div className="grid grid-cols-1 lg:grid-cols-3 gap-6">
            {/* Animation Presets */}
            <div className="lg:col-span-2 space-y-4">
              <Card className="bg-zinc-900 border-zinc-800">
                <CardHeader>
                  <CardTitle className="flex items-center gap-2">
                    <Film className="w-5 h-5" />
                    Animation Presets
                  </CardTitle>
                  <CardDescription>
                    Select a preset to animate your character
                  </CardDescription>
                </CardHeader>
                <CardContent>
                  {Object.entries(presets).map(([category, categoryPresets]) => (
                    <div key={category} className="mb-6">
                      <h3 className="text-sm font-semibold text-zinc-300 mb-3 flex items-center gap-2">
                        <span>{CATEGORY_ICONS[category] || '🎬'}</span>
                        <span className="capitalize">{category}</span>
                      </h3>
                      <div className="grid grid-cols-2 md:grid-cols-3 gap-2">
                        {categoryPresets.map((preset) => (
                          <button
                            key={preset.id}
                            onClick={() => setSelectedPreset(preset)}
                            className={`p-3 rounded-lg border text-left transition-all ${
                              selectedPreset?.id === preset.id 
                                ? 'border-orange-500 bg-orange-500/10' 
                                : 'border-zinc-700 bg-zinc-800 hover:border-zinc-500'
                            }`}
                          >
                            <p className="font-medium text-sm">{preset.name}</p>
                            <p className="text-xs text-zinc-500">{preset.frames} frames</p>
                          </button>
                        ))}
                      </div>
                    </div>
                  ))}
                </CardContent>
              </Card>
            </div>
            
            {/* Preview & Controls */}
            <div className="space-y-4">
              {/* Download Puppet File */}
              {puppetFile && (
                <Card className="bg-zinc-900 border-zinc-800">
                  <CardHeader>
                    <CardTitle className="flex items-center gap-2">
                      <Download className="w-5 h-5" />
                      Puppet File
                    </CardTitle>
                    <CardDescription>
                      Download the .blend file to view and edit in Blender
                    </CardDescription>
                  </CardHeader>
                  <CardContent>
                    <a 
                      href={puppetFile} 
                      download={`${characterName}_puppet.blend`}
                      className="block"
                    >
                      <Button variant="outline" className="w-full">
                        <Download className="w-4 h-4 mr-2" />
                        Download .blend File
                      </Button>
                    </a>
                    <p className="text-xs text-zinc-500 mt-2">
                      Open in Blender 3.4+ to adjust positioning, add custom animations, etc.
                    </p>
                  </CardContent>
                </Card>
              )}
              
              {/* Selected Animation */}
              <Card className="bg-zinc-900 border-zinc-800">
                <CardHeader>
                  <CardTitle>Selected Animation</CardTitle>
                </CardHeader>
                <CardContent>
                  {selectedPreset ? (
                    <div className="space-y-3">
                      <div className="p-4 bg-zinc-800 rounded-lg">
                        <h3 className="font-semibold">{selectedPreset.name}</h3>
                        <p className="text-sm text-zinc-400 mt-1">{selectedPreset.description}</p>
                        <div className="flex gap-2 mt-2">
                          <Badge variant="secondary">{selectedPreset.frames} frames</Badge>
                          {selectedPreset.loop && <Badge variant="outline">Loops</Badge>}
                        </div>
                      </div>
                      
                      <Button 
                        onClick={() => animatePuppet(selectedPreset.id)}
                        disabled={loading}
                        className="w-full"
                      >
                        {loading ? (
                          <><Loader2 className="w-4 h-4 animate-spin mr-2" /> Rendering...</>
                        ) : (
                          <><Play className="w-4 h-4 mr-2" /> Render Animation</>
                        )}
                      </Button>
                    </div>
                  ) : (
                    <p className="text-zinc-500 text-center py-4">
                      Select a preset to animate
                    </p>
                  )}
                </CardContent>
              </Card>
              
              {/* Rendered Animations */}
              <Card className="bg-zinc-900 border-zinc-800">
                <CardHeader>
                  <CardTitle>Rendered Animations</CardTitle>
                </CardHeader>
                <CardContent>
                  {Object.entries(animations).length > 0 ? (
                    <div className="space-y-2">
                      {Object.entries(animations).map(([id, anim]) => (
                        <div key={id} className="p-3 bg-zinc-800 rounded-lg">
                          <div className="flex items-center justify-between">
                            <span className="text-sm">{anim.preset_name}</span>
                            {anim.status === 'rendering' ? (
                              <Loader2 className="w-4 h-4 animate-spin text-orange-400" />
                            ) : anim.status === 'complete' ? (
                              <Check className="w-4 h-4 text-green-400" />
                            ) : (
                              <AlertCircle className="w-4 h-4 text-red-400" />
                            )}
                          </div>
                          {anim.status === 'failed' && anim.error && (
                            <p className="text-xs text-red-400 mt-1">{anim.error.slice(0, 100)}</p>
                          )}
                          {anim.video_url && (
                            <a 
                              href={anim.video_url} 
                              target="_blank" 
                              rel="noopener noreferrer"
                              className="text-xs text-blue-400 hover:underline mt-1 block"
                            >
                              Download Video
                            </a>
                          )}
                          {anim.status === 'failed' && (
                            <Button 
                              variant="outline" 
                              size="sm" 
                              className="mt-2 w-full text-xs h-7"
                              onClick={() => animatePuppet(anim.preset_id)}
                              disabled={loading}
                            >
                              <RefreshCw className="w-3 h-3 mr-1" /> Retry
                            </Button>
                          )}
                        </div>
                      ))}
                    </div>
                  ) : (
                    <p className="text-zinc-500 text-center py-4">
                      No animations rendered yet
                    </p>
                  )}
                </CardContent>
              </Card>
              
              <Button variant="outline" onClick={() => setMode('segments')} className="w-full">
                <ChevronLeft className="w-4 h-4 mr-1" /> Back to Segments
              </Button>
            </div>
          </div>
        )}
      </div>
    </div>
  );
}
